import axios, { CancelTokenSource } from 'axios';
import { action, autorun, computed, makeObservable, observable, runInAction } from 'mobx';

import { injectable } from '@servicetitan/react-ioc';

import { BattlecardStore } from './battlecard.store';
import { SelectedEquipmentInfo } from './chat.store';
import { AppointmentsApi } from '../api/appointments.api';
import {
    EquipmentsApi,
    InstalledEquipmentApiModel,
    MappedEquipmentApiModel,
} from '../api/equipments.api';
import { JobDetailsApi, JobDetailsApiModel } from '../api/job-details.api';
import { JobsApi } from '../api/jobs.api';
import { TechniciansApi } from '../api/technicians.api';
import { TopicsApi } from '../api/topics.api';
import { AppointmentViewModel, toAppointmentViewModel } from '../models/job.model';
import { TopicViewModel, toTopicViewModel } from '../models/topic.model';
import { getBaseRequestParams } from '../utils/base-request-params';

@injectable()
export class MainStore {
    @observable selectedAppointment?: AppointmentViewModel;
    @observable selectedTopic?: TopicViewModel;
    @observable jobDetails?: JobDetailsApiModel;
    @observable technicianPermissions?: number[];

    @observable selectedMonolithEquipment?: InstalledEquipmentApiModel;
    @observable selectedMappedEquipments?: MappedEquipmentApiModel[];

    @observable showCloseInChatPage?: boolean = undefined;

    readonly waitForInit: Promise<void>;
    private resolveInit!: () => void;

    private loadJobsCancelToken?: CancelTokenSource = undefined;
    private loadJobDetailsCancelToken?: CancelTokenSource = undefined;
    private loadTechnicianPermissionsCancelToken?: CancelTokenSource = undefined;
    private loadInstalledEquipmentCancelToken?: CancelTokenSource = undefined;
    private loadInstalledEquipmentMappingsCancelToken?: CancelTokenSource = undefined;

    constructor(
        private readonly jobApi: JobsApi,
        private readonly appointmentsApi: AppointmentsApi,
        private readonly jobDetailsApi: JobDetailsApi,
        private readonly techniciansApi: TechniciansApi,
        private readonly topicsApi: TopicsApi,
        private readonly equipmentApi: EquipmentsApi,
        private readonly battlecardStore: BattlecardStore
    ) {
        makeObservable(this);
        this.waitForInit = new Promise(resolve => {
            this.resolveInit = resolve;
        });

        autorun(() => {
            if (this.selectedAppointment) {
                this.battlecardStore.initBattlecard(this.selectedAppointment?.id);
            }
        });

        this.loadTechnicianPermissions();
    }

    @computed
    get equipmentHasDocumentation(): boolean {
        const docs = this.selectedMappedEquipments?.flatMap(x => x.documents) ?? [];

        return docs?.length > 0;
    }

    @computed
    get selectedEquipmentInfo(): SelectedEquipmentInfo | undefined {
        if (!this.selectedMonolithEquipment && (this.selectedMappedEquipments ?? []).length === 0) {
            return undefined;
        }

        if (this.selectedMonolithEquipment) {
            return { ...this.selectedMonolithEquipment };
        }

        const mapped = this.selectedMappedEquipments?.[0];
        if (!mapped) {
            return undefined;
        }
        return {
            name: mapped.name,
            manufacturer: mapped.manufacturer.text,
        };
    }

    @computed
    get hasPermissionToAddNote() {
        // return true;
        return this.technicianPermissions?.includes(1133) ?? false;
    }

    @computed
    get hasPermissionToAddInvoiceItem() {
        // return true;
        return this.technicianPermissions?.includes(1157) ?? false;
    }

    @computed
    get hasPermissionToAddEstimateItem() {
        // return true;
        return this.technicianPermissions?.includes(1157) ?? false;
    }

    initByParams = async (
        appointmentIdString?: string,
        topicIdString?: string,
        equipmentId?: string
    ) => {
        const appointmentId = Number(appointmentIdString);

        if (appointmentId && !Number.isNaN(appointmentId)) {
            if (!this.selectedAppointment || this.selectedAppointment.id !== appointmentId) {
                await this.loadJob(appointmentId);
                try {
                    // Fetch job details
                    await this.loadJobDetails(appointmentId);
                } catch {
                    // skip
                }
            }
        }
        // job should be selected
        if (this.selectedAppointment) {
            const topicId = Number(topicIdString);
            if (topicId && !Number.isNaN(topicId)) {
                if (!this.selectedTopic || this.selectedTopic.id !== topicId) {
                    await this.loadTopic(topicId);
                }
            } else {
                this.showCloseInChatPage ??= false;
            }
        }

        if (
            equipmentId &&
            equipmentId !== 'equipment-picker' &&
            this.selectedAppointment?.locationId
        ) {
            const locationId = this.selectedAppointment?.locationId;
            // check if equipment is installed equipment
            let mappedIds: string[] = [];

            if (!Number.isNaN(Number(equipmentId))) {
                // fetch equipment info
                await this.loadMonolithEquipment(locationId, Number(equipmentId));
                // get mappedIds
                mappedIds = this.selectedMonolithEquipment?.mapped_equipment_ids ?? [];
            } else {
                // mapped equipment ids
                mappedIds = equipmentId.split(',');
            }

            // fetch by mappedIds
            await this.loadMappedEquipment(locationId, mappedIds);
            runInAction(() => {
                this.showCloseInChatPage ??= true;
            });
        }

        runInAction(() => {
            if (this.showCloseInChatPage !== true) {
                this.showCloseInChatPage = false;
            }
        });

        this.resolveInit();

        return {
            selectedJob: this.selectedAppointment,
            selectedTopic: this.selectedTopic,
            selectedMonolithEquipment: this.selectedMonolithEquipment,
            selectedMappedEquipments: this.selectedMappedEquipments,
        };
    };

    @action
    loadJob = async (appointmentId: number) => {
        if (this.loadJobsCancelToken) {
            this.loadJobsCancelToken?.cancel();
        }
        this.loadJobsCancelToken = axios.CancelToken.source();
        try {
            /*
             * const { data } = await this.jobApi.getJobs(
             *     {
             *         ...getBaseRequestParams(),
             *         // eslint-disable-next-line @typescript-eslint/naming-convention
             *         job_id: appointmentId,
             *     },
             *     this.loadJobsCancelToken.token
             * );
             * const job = data.jobs.find(x => x.job_id === appointmentId);
             * if (job) {
             *     this.setSelectedAppointment(toJobViewModel(job));
             * }
             */

            const { data: appointmentData } =
                await this.appointmentsApi.getAppointmentDetails(appointmentId);
            this.setSelectedAppointment(toAppointmentViewModel(appointmentData));
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return;
            }
            throw error;
        } finally {
            this.loadJobsCancelToken = undefined;
        }
    };

    @action
    loadJobDetails = async (appointmentId: number) => {
        this.loadJobDetailsCancelToken?.cancel();
        this.loadJobDetailsCancelToken = axios.CancelToken.source();
        try {
            const { data } = await this.jobDetailsApi.getJobDetails(
                appointmentId,
                this.loadJobDetailsCancelToken.token
            );
            runInAction(() => {
                this.jobDetails = data;
            });
        } catch (error) {
            if (axios.isCancel(error)) {
                // skip handling cancel error
                return;
            }
            if (axios.isAxiosError(error)) {
                runInAction(() => {
                    this.jobDetails = undefined;
                });
                return;
            }
            throw error;
        } finally {
            this.loadJobDetailsCancelToken = undefined;
        }
    };

    @action
    loadTechnicianPermissions = async () => {
        this.loadTechnicianPermissionsCancelToken?.cancel();
        this.loadTechnicianPermissionsCancelToken = axios.CancelToken.source();
        try {
            const { data } = await this.techniciansApi.getTechnicianPermissions(
                this.loadTechnicianPermissionsCancelToken.token
            );

            runInAction(() => {
                this.technicianPermissions = data.permissions;
            });
        } catch (error) {
            if (axios.isCancel(error)) {
                // skip handling cancel error
                return;
            }
            if (axios.isAxiosError(error)) {
                runInAction(() => {
                    this.technicianPermissions = undefined;
                });
                return;
            }
            throw error;
        } finally {
            this.loadTechnicianPermissionsCancelToken = undefined;
        }
    };

    @action
    loadTopic = async (topicId: number) => {
        try {
            const [
                { data: topicsData },
                {
                    data: { isEnabled: isFieldProEnabled },
                },
                isFieldProCoachingEnabled,
            ] = await Promise.all([
                this.topicsApi.getTopics(),
                this.techniciansApi.getFieldAssistProEnabled(),
                this.getFieldProCoachingEnabledSafe(),
            ]);

            const topic = topicsData.topics.find(x => x.id === topicId);
            if (!topic || topic?.disabled) {
                this.clearSelectedTopic();
            } else {
                this.setSelectedTopic(
                    toTopicViewModel(topic, isFieldProEnabled, isFieldProCoachingEnabled)
                );
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return;
            }
            throw error;
        }
    };

    @action
    loadMonolithEquipment = async (locationId: number, equipmentId: number) => {
        this.loadInstalledEquipmentCancelToken?.cancel();

        this.loadInstalledEquipmentCancelToken = axios.CancelToken.source();
        try {
            const { data } = await this.equipmentApi.getLocationEquipments(
                {
                    ...getBaseRequestParams(),
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    location_id: locationId,
                },
                this.loadInstalledEquipmentCancelToken.token
            );
            const equipment = data.equipments.find(x => x.equipment_id === equipmentId);
            if (equipment) {
                this.setSelectedMonolithInstalledEquipment(equipment);
            } else {
                this.clearSelectedMonolithInstalledEquipment();
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return;
            }
            throw error;
        } finally {
            this.loadInstalledEquipmentCancelToken = undefined;
        }
    };

    @action
    loadMappedEquipment = async (locationId: number, mappedIds: string[]) => {
        this.loadInstalledEquipmentMappingsCancelToken?.cancel();
        try {
            this.loadInstalledEquipmentMappingsCancelToken = axios.CancelToken.source();
            if (mappedIds.length > 0) {
                const { data } = await this.equipmentApi.getEquipmentInfo(
                    {
                        ...getBaseRequestParams(),
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        location_id: locationId,
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        mapped_equipment_ids: mappedIds,
                    },
                    this.loadInstalledEquipmentMappingsCancelToken.token
                );
                this.setSelectedMappedEquipments(data.mapped_equipments);
            } else {
                this.setSelectedMappedEquipments([]);
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return;
            }
            throw error;
        } finally {
            this.loadInstalledEquipmentCancelToken = undefined;
        }
    };

    @action
    setSelectedAppointment(job: AppointmentViewModel) {
        this.selectedAppointment = job;
    }

    // ---------------
    @action
    setSelectedTopic(topic: TopicViewModel) {
        this.selectedTopic = topic;
    }

    @action
    clearSelectedTopic() {
        this.selectedTopic = undefined;
    }

    // ---------------
    @action
    setSelectedMonolithInstalledEquipment(equipment: InstalledEquipmentApiModel) {
        this.selectedMonolithEquipment = equipment;
    }
    @action
    clearSelectedMonolithInstalledEquipment() {
        this.selectedMonolithEquipment = undefined;
    }

    // ---------------
    @action
    setSelectedMappedEquipments(equipments: MappedEquipmentApiModel[]) {
        this.selectedMappedEquipments = equipments;
    }
    @action
    clearSelectedMappedEquipments() {
        this.selectedMappedEquipments = undefined;
    }

    @action
    resetSelected() {
        this.selectedMonolithEquipment = undefined;
        this.selectedMappedEquipments = undefined;
        this.selectedTopic = undefined;
    }

    /*
     * Fail-soft entitlement check: a Field-Pro-Coaching-specific outage must not abort topic
     * selection for non-coaching topics (the BFF already fails closed for salespro-web errors;
     * this covers the BFF boundary itself).
     */
    private getFieldProCoachingEnabledSafe = async () => {
        try {
            const { data } = await this.techniciansApi.getFieldProTopicAvailability();
            return data.isEnabled;
        } catch (err) {
            if (axios.isCancel(err)) {
                throw err;
            }
            return false;
        }
    };
}
